import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Tooltip, DatePicker, Select, message } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";
import { paginate } from "../../../utlits/Paginate.js";
import Pagination from "../../Reuseable/Pagination";
import DataParser from "../../../utlits/Dataparser";
import WfhService from "../../../Services/WfhService";
import StatusBadgeHr from "../../Reuseable/StatusBadgeHr";
import UserProfile from "../../Reuseable/UserProfile..jsx";
import UserServices from "../../../Services/UserService";
import SocketService from "../../../Services/SocketService";
import TableSkeleton from "../../Reuseable/TableSkeleton";

const WfhApprovals = () => {
    const { RangePicker } = DatePicker;
    const { Option } = Select;

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [applications, setApplications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateRangeFilter, setDateRangeFilter] = useState(null);
    const [userFilter, setUserFilter] = useState("all");
    const [hierarchyUsers, setHierarchyUsers] = useState([]);

    // Modal Details States
    const [viewedApp, setViewedApp] = useState(null);

    const companyId = DataParser.CompanyId();
    const userId = DataParser.UserId();

    const fetchApplications = async () => {
        setIsLoading(true);
        try {
            const res = await WfhService.getDeptUsers({ company_id: companyId, id: userId });
            if (res.Good) {
                setApplications(res.data.wfh_applications || []);
            }
        } catch (err) {
            console.error("Error fetching WFH approvals:", err);
            if (!err.response || err.response.status >= 500) {
                message.error("Failed to load WFH applications");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fetchHierarchyUsers = async () => {
        try {
            const res = await UserServices.getWfhTeamUsernames(companyId);
            if (res.Good) {
                setHierarchyUsers(res.data.user || []);
            }
        } catch (err) {
            console.error("Error fetching WFH Team users:", err);
        }
    };

    useEffect(() => {
        fetchApplications();
        fetchHierarchyUsers();

        const handleWfhUpdate = (data) => {
            if (String(data.company_id) === String(companyId)) {
                fetchApplications();
                if (data.action === "create") {
                    fetchHierarchyUsers();
                }
            }
        };

        SocketService.on("wfh_update", handleWfhUpdate);
        return () => {
            SocketService.off("wfh_update", handleWfhUpdate);
        };
    }, [companyId]);

    const handlePageChange = (e, page) => {
        e.preventDefault();
        setCurrentPage(page);
    };

    const handleStatusUpdateApi = async (id, status) => {
        try {
            const res = await WfhService.updateStatus(id, { status, company_id: companyId });
            if (res.Good) {
                message.success(res.data || "Status updated");
                fetchApplications();
            } else {
                message.error(res.data || "Update failed");
            }
        } catch (err) {
            console.error(err);
            message.error("Server error");
        }
    };

    const handleViewDetails = (app) => {
        setViewedApp(app);
        const modal = new window.bootstrap.Modal(document.getElementById("detailsModal"));
        modal.show();
    };

    const getFilteredData = () => {
        let filtered = [...applications];
        if (statusFilter !== "all") filtered = filtered.filter(item => String(item.status) === statusFilter);
        if (userFilter !== "all") filtered = filtered.filter(item => String(item?.user?.id) === userFilter);
        if (dateRangeFilter && dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            filtered = filtered.filter(item => {
                const itemStart = moment(item.from_date);
                const itemEnd = moment(item.to_date);
                return (
                    (itemStart.isSameOrAfter(start, 'day') && itemStart.isSameOrBefore(end, 'day')) ||
                    (itemEnd.isSameOrAfter(start, 'day') && itemEnd.isSameOrBefore(end, 'day')) ||
                    (itemStart.isBefore(start, 'day') && itemEnd.isAfter(end, 'day'))
                );
            });
        }
        return filtered;
    };

    const filteredData = getFilteredData();
    const paginatedData = paginate(filteredData, currentPage, pageSize);

    return (
        <>
            <Helmet>
                <title>Team WFH Requests - Planit</title>
            </Helmet>

            <div className="main-content">
                <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 px-0">
                                <style>{`
                                    /* KILL ALL ROUNDED CORNERS AND SHADOWS */
                                    .card, .card-body, .table-responsive, .listing-table-wrapper, 
                                    .listing-table, .listing-table thead, .listing-table tr, 
                                    .listing-table th, .listing-table td {
                                        border-radius: 0 !important;
                                        box-shadow: none !important;
                                        border-left: none !important;
                                        border-right: none !important;
                                        border-top: none !important;
                                    }

                                    /* Match Evaluation Table exact geometry */
                                    .listing-table thead tr.tableHeaderbg th {
                                        background-color: #F2F2F2 !important;
                                        color: #212529 !important;
                                        border-bottom: none !important;
                                        font-weight: 600 !important;
                                        font-size: 13px !important;
                                        padding: 8px 12px !important;
                                    }

                                    .listing-table tbody tr td {
                                        border-bottom: 1px solid #F2F2F2 !important;
                                        vertical-align: middle !important;
                                    }

                                    .listing-table tbody tr:last-child td {
                                        border-bottom: none !important;
                                    }

                                    /* Filter Row Pattern Synced */
                                    .custom-filter.float-select-wrap .ant-select-selector,
                                    .custom-filter.float-select-wrap .ant-picker {
                                        height: 34px !important;
                                        min-height: 34px !important;
                                        border-radius: 6px !important;
                                    }
                                    
                                    .custom-filter.float-select-wrap .ant-select-selection-item,
                                    .custom-filter.float-select-wrap .ant-select-selection-placeholder,
                                    .custom-filter.float-select-wrap .ant-picker-input > input {
                                        line-height: 30px !important;
                                        font-size: 13px !important;
                                    }

                                    .custom-filter.float-select-wrap label {
                                        font-size: 11px !important;
                                        font-weight: 600 !important;
                                    }

                                    .ant-picker-range .ant-picker-active-bar {
                                        background: #1D1D1D !important;
                                    }

                                    /* Hover/focus on filter fields */
                                    .custom-filter.float-select-wrap:hover .ant-select-selector,
                                    .custom-filter.float-select-wrap:focus-within .ant-select-selector {
                                        border-color: #1D1D1D !important; box-shadow: none !important;
                                    }
                                    .custom-filter.float-select-wrap:hover .ant-picker,
                                    .custom-filter.float-select-wrap .ant-picker-focused {
                                        border-color: #1D1D1D !important; box-shadow: none !important;
                                    }
                                    .custom-filter.float-select-wrap:hover label,
                                    .custom-filter.float-select-wrap:focus-within label { color: #1D1D1D !important; }
                                    .custom-filter.float-select-wrap:hover .ant-select-arrow,
                                    .custom-filter.float-select-wrap:focus-within .ant-select-arrow { color: #1D1D1D !important; }
                                    .custom-filter.float-select-wrap:hover .ant-picker-suffix div,
                                    .custom-filter.float-select-wrap:focus-within .ant-picker-suffix div { background-color: #1D1D1D !important; }
                                    .custom-filter.float-select-wrap:hover .ant-picker-suffix div i,
                                    .custom-filter.float-select-wrap:focus-within .ant-picker-suffix div i { color: #fff !important; }
                                `}</style>

                                <div className="d-flex align-items-center justify-content-end" style={{ padding: "0 0 12px 0" }}>

                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <div className={`float-select-wrap custom-filter ${userFilter !== "all" ? "has-value" : ""}`} style={{ width: "220px", maxWidth: "220px" }}>
                                            <label>Employee</label>
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder=""
                                                value={userFilter === "all" ? null : userFilter}
                                                onChange={(val) => setUserFilter(val || "all")}
                                                dropdownStyle={{ zIndex: 1050 }}
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                <Option value="all" label="All">All</Option>
                                                {hierarchyUsers.map(u => (
                                                    <Option key={u.id} value={String(u.id)} label={u.full_name || u.name}>
                                                        {u.full_name || u.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </div>

                                        <div className={`float-select-wrap custom-filter ${statusFilter !== "all" ? "has-value" : ""}`} style={{ width: "180px", maxWidth: "180px" }}>
                                            <label>Status</label>
                                            <Select
                                                allowClear
                                                placeholder=""
                                                value={statusFilter === "all" ? null : statusFilter}
                                                onChange={(val) => setStatusFilter(val || "all")}
                                                dropdownStyle={{ zIndex: 1050 }}
                                            >
                                                <Option value="all" label="All">All</Option>
                                                <Option value="0" label="Pending">Pending</Option>
                                                <Option value="1" label="Approved">Approved</Option>
                                                <Option value="2" label="Disapproved">Disapproved</Option>
                                            </Select>
                                        </div>

                                        <div className={`float-select-wrap custom-filter ${dateRangeFilter ? "has-value" : ""}`} style={{ width: "280px", maxWidth: "280px" }}>
                                            <label>WFH Period</label>
                                            <RangePicker
                                                value={dateRangeFilter}
                                                onChange={setDateRangeFilter}
                                                format="DD/MM/YYYY"
                                                allowClear
                                                separator=""
                                                placeholder={['', '']}
                                                suffixIcon={
                                                    <div style={{
                                                        backgroundColor: "#F4F4F4",
                                                        borderRadius: "6px",
                                                        width: "24px",
                                                        height: "24px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        marginRight: "-4px"
                                                    }}>
                                                        <i className="uil-schedule" style={{ color: "#A0A0A0", fontSize: "15px" }} />
                                                    </div>
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive listing-table-wrapper">
                                            <table className="table mb-0 listing-table custom-table">
                                                <thead>
                                                    <tr className="tableHeaderbg">
                                                        <th className="text-start ps-4">Employee</th>
                                                        <th className="text-start">Department</th>
                                                        <th className="text-start">Start Date</th>
                                                        <th className="text-start">End Date</th>
                                                        <th className="text-start">Total Days</th>
                                                        <th className="text-start">Status</th>
                                                        <th className="text-center pe-4">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isLoading ? (
                                                        <TableSkeleton cols={7} rows={6} />
                                                    ) : (paginatedData.length > 0 ? (
                                                        paginatedData.map((el, index) => (
                                                            <tr key={index} className="align-middle">
                                                                <td className="ps-4">
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <UserProfile
                                                                            username={el?.user?.name}
                                                                            userImage={el?.user?.image}
                                                                            style={{ width: '22px', height: '22px' }}
                                                                        />
                                                                        <span>{el?.user?.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{el?.user?.departmentName || "-"}</td>
                                                                <td>
                                                                    <span><i className="uil uil-calendar-alt me-1" style={{ color: "#0DDFAB", fontSize: "14px" }}></i> {moment(el.from_date).format("D, MMMM")}</span>
                                                                </td>
                                                                <td>
                                                                    <span><i className="uil uil-calendar-alt me-1" style={{ color: "#0DDFAB", fontSize: "14px" }}></i> {moment(el.to_date).format("D, MMMM")}</span>
                                                                </td>
                                                                <td>{el.total_days}</td>
                                                                <td>
                                                                    <StatusBadgeHr
                                                                        item={el}
                                                                        api={handleStatusUpdateApi}
                                                                        hideLateAbsent={true}
                                                                    />
                                                                </td>
                                                                <td className="text-center pe-4">
                                                                    <Link
                                                                        to="#"
                                                                        className="cursor-pointer iconscolor"
                                                                        onClick={() => handleViewDetails(el)}
                                                                    >
                                                                        <Tooltip title="View WFH details">
                                                                            <i className="uil uil-eye iconscolor" style={{ fontSize: "18px" }} />
                                                                        </Tooltip>
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-4">No WFH applications found.</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {filteredData.length > pageSize && (
                                                <div className="d-flex justify-content-center mt-4 pb-4">
                                                    <Pagination
                                                        itemCount={filteredData.length}
                                                        currentPage={currentPage}
                                                        pageSize={pageSize}
                                                        onPageChange={handlePageChange}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal fade" id="detailsModal" tabIndex="-1" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content p-0" style={{ borderRadius: "12px", overflow: "hidden", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
                            <div className="modal-header border-0 px-4 py-3 bg-white pb-0">
                                <h5 className="modal-title fw-bold" style={{ color: "#1D1D1D", fontSize: "1.1rem" }}>WFH Request Details</h5>
                                <button type="button" className="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div className="modal-body p-4 bg-white">
                                {viewedApp && (
                                    <div className="leave-details">
                                        {[
                                            ["Employee", <div className="d-flex align-items-center gap-2"><UserProfile username={viewedApp.user?.name} userImage={viewedApp.user?.image} /> <span>{viewedApp.user?.name}</span></div>],
                                            ["WFH Type", <span className="fw-medium">Work From Home</span>],
                                            ["Department", viewedApp.user?.departmentName || "-"],
                                            ["From", moment(viewedApp.from_date).format("DD/MM/YYYY")],
                                            ["To", moment(viewedApp.to_date).format("DD/MM/YYYY")],
                                            ["Total Days", <span className="fw-bold text-dark">{viewedApp.total_days}</span>],
                                            ["Status", viewedApp.status === 0 || viewedApp.status === null ? (
                                                <span className="badge px-3" style={{ backgroundColor: "#fdfd96", color: "#856404", border: "1px solid #ced4da", borderRadius: "5px" }}>Pending</span>
                                            ) : viewedApp.status === 1 ? (
                                                <span className="badge px-3" style={{ backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb", borderRadius: "5px" }}>Approved</span>
                                            ) : (
                                                <span className="badge px-3" style={{ backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", borderRadius: "5px" }}>Disapproved</span>
                                            )],
                                            ["Action Taken By", <span className="text-muted">{(viewedApp.status === 1 || viewedApp.status === 2) ? (viewedApp.approved_by_user || viewedApp.rejected_by_user || "-") : "-"}</span>],
                                            ["Reason", <div style={{ fontSize: "0.9rem", color: "#666" }}>{viewedApp.reason}</div>]
                                        ].map(([label, value], idx, arr) => (
                                            <div key={idx} className={`mb-3 d-flex flex-wrap align-items-start ${idx === arr.length - 1 ? "" : "border-bottom"} pb-2`}>
                                                <div style={{ minWidth: "140px", fontWeight: "600", color: "#495057", fontSize: "0.9rem" }}>{label}:</div>
                                                <div className="flex-grow-1" style={{ color: "#212529", lineHeight: "1.5", wordBreak: "break-word", fontSize: "0.9rem" }}>{value}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WfhApprovals;
